import React, { useEffect, useRef, useState } from 'react';
import { getPublicConfig } from '../services/publicConfig';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-cloudflare-turnstile]');

      if (existing) {
        if (window.turnstile) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')));
        return;
      }

      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.dataset.cloudflareTurnstile = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Turnstile failed to load'));
      document.head.appendChild(script);
    }).catch((error) => {
      scriptPromise = null;
      throw error;
    });
  }

  return scriptPromise;
}

export default function TurnstileWidget({ onToken, onConfigured }) {
  const containerRef = useRef(null);
  const [error, setError] = useState('');
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let widgetId = null;

    getPublicConfig()
      .then((config) => {
        if (cancelled) return;

        const siteKey = config.turnstileSiteKey;
        if (onConfigured) onConfigured(Boolean(siteKey));
        if (!siteKey) return;

        return loadTurnstileScript().then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;

          widgetId = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme: 'dark',
            size: 'compact',
            appearance: 'interaction-only',
            callback: (token) => {
              if (cancelled) return;
              setError('');
              onToken(token);
            },
            'expired-callback': () => {
              if (!cancelled) onToken('');
            },
            'before-interactive-callback': () => {
              if (!cancelled) setInteractive(true);
            },
            'after-interactive-callback': () => {
              if (!cancelled) setInteractive(false);
            },
            'error-callback': (code) => {
              if (cancelled) return true;
              onToken('');
              if (code === '110200') {
                setError('Verification is not configured for this domain. Please contact us directly.');
              } else {
                setError('Verification failed. Please refresh and try again.');
              }
              return true;
            },
          });
        });
      })
      .catch(() => {
        if (cancelled) return;
        setError('Verification could not load. Please refresh and try again.');
        if (onConfigured) onConfigured(true);
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [onToken, onConfigured]);

  if (error) {
    return <p className="text-sm text-red-300">{error}</p>;
  }

  return (
    <div className={`turnstile-widget relative inline-block overflow-hidden rounded-lg ${interactive ? 'bg-ink-light/60 opacity-90' : ''}`}>
      <div ref={containerRef} className="flex justify-center" />
      {interactive && (
        <span className="pointer-events-none absolute inset-0 rounded-lg border border-gray-400/50" />
      )}
    </div>
  );
}
